/* eslint-disable max-len */
import { AxiosRequestConfig } from "axios";
import { StoreApi } from "zustand";

import { IAsset } from "@/v1/components/pages/GameDetail/components/AssetsTab/types/Asset.interface";
import { AssetStatus } from "@/v1/components/pages/GameDetail/components/AssetsTab/utils/constants";
import { createAsset } from "@/v1/components/pages/GameDetail/components/AssetsTab/utils/createAsset";
import { createFolder } from "@/v1/components/pages/GameDetail/components/AssetsTab/utils/createFolder";
import { createUploadAsset } from "@/v1/components/pages/GameDetail/components/AssetsTab/utils/createUploadAsset";
import { downloadAsset, downloadAWSDataService, uploadAsset } from "@/v1/services/assets";
import { downloadBlobData } from "@/v1/utils/downloadBlobData";
import { TasksLinksType } from "@/v2/utils/constants";
import { createStore } from "@/v2/utils/createStore";

import { addAssetToQueue } from "./utils/addAssetToQueue";
import { catchAssetRequest } from "./utils/catchAssetRequest";
import { MIN_DOWNLOAD_PERCENTAGE } from "./utils/constants";
import { handleRequestProgress } from "./utils/handleRequestProgress";
import { updateQueueAsset } from "./utils/updateQueueAsset";

type WaitingQueue = [IAsset, () => Promise<any>, { onEnd?: () => void; onError?: () => void }?][];

export type StateType = {
  set: StoreApi<IUseUploadAssetsStore>["setState"];
  get: StoreApi<IUseUploadAssetsStore>["getState"];
  waitingQueue: WaitingQueue;
};

interface IUseUploadAssetsStore {
  queue: IAsset[];
  activeRequests: IAsset[];
  removeAllRequests: () => void;
  uploadFile: (gameId: string, file: File, path: string) => Promise<void>;
  uploadFolder: (gameId: string, folderName: string, filesData: [File, string][]) => Promise<void>;
  removeAsset: (asset: IAsset) => void;
  downloadAsset: (
    name: string,
    path: string,
    axiosConfig?: AxiosRequestConfig,
    triggerWatcher?: boolean,
  ) => Promise<void>;
}

// eslint-disable-next-line max-lines-per-function
export const useRequestsStore = createStore<IUseUploadAssetsStore>((set, get) => {
  let waitingQueue: WaitingQueue = [];

  const state = { set, get, waitingQueue };

  return {
    activeRequests: [],
    queue: [],
    removeAsset: (asset: IAsset) => {
      asset.requestAbortController.abort();

      waitingQueue = waitingQueue.filter(([queueAsset]) => queueAsset.id !== asset.id);

      set(({ queue }) => {
        const newQueue = queue.filter((queueAsset) => {
          if (asset.isFolder && queueAsset.folderName) {
            return asset.name !== queueAsset.folderName;
          }
          return queueAsset.id !== asset.id;
        });

        return {
          queue: newQueue,
          activeRequests: newQueue,
        };
      });
    },
    removeAllRequests: () => {
      const activeRequests = get().activeRequests;

      activeRequests.forEach((asset) => {
        asset.requestAbortController.abort();
      });

      waitingQueue = [];
      set({ activeRequests: [], queue: [] });
    },
    uploadFolder: async (gameId, folderName, filesData) => {
      const folder = { ...createFolder(folderName), status: AssetStatus.IN_PROGRESS };

      addAssetToQueue(folder, state);

      let uploadDoneCount = 0;
      let uploadErrorCount = 0;
      filesData.forEach(([file, path]) => {
        const newAsset = createUploadAsset(file, path, folderName);

        addAssetToQueue(newAsset, state);

        catchAssetRequest(
          newAsset,
          async () => {
            return uploadAsset(gameId, newAsset);
          },
          state,
          {
            onError: () => {
              uploadErrorCount++;
            },
            onEnd: () => {
              uploadDoneCount++;
              const newReqPercentage = (100 * uploadDoneCount) / filesData.length;
              const uploadEnded = newReqPercentage === 100;
              const endStatus = uploadErrorCount > 0 ? AssetStatus.ERROR : AssetStatus.SUCCESS;

              updateQueueAsset(
                folder,
                {
                  ...folder,
                  requestPecentage:
                    newReqPercentage < MIN_DOWNLOAD_PERCENTAGE
                      ? MIN_DOWNLOAD_PERCENTAGE
                      : newReqPercentage,
                  status: uploadEnded ? endStatus : AssetStatus.IN_PROGRESS,
                },
                state,
              );
            },
          },
        );
      });
    },
    uploadFile: async (gameId, file, path) => {
      const newAsset = createUploadAsset(file, path);

      addAssetToQueue(newAsset, state);

      await catchAssetRequest(
        newAsset,
        async () => {
          await uploadAsset(gameId, newAsset, {
            onUploadProgress: handleRequestProgress(newAsset, state),
            signal: newAsset.requestAbortController.signal,
          });
        },
        state,
      );
    },
    downloadAsset: async (
      name: string,
      path: string,
      axiosConfig?: AxiosRequestConfig,
      triggerWatcher = true,
    ) => {
      const newAsset = createAsset(name, path);
      if (!TasksLinksType.includes(name)) {
        addAssetToQueue(newAsset, state, triggerWatcher);
      }

      await catchAssetRequest(
        newAsset,
        async () => {
          const res = await downloadAsset(path, axiosConfig);

          let blobData = res.data;

          if (res.data.url) {
            blobData = (
              await downloadAWSDataService(res.data.url, {
                onDownloadProgress: handleRequestProgress(newAsset, state),
                signal: newAsset.requestAbortController.signal,
              })
            ).data;
          }

          downloadBlobData(blobData, newAsset.name);
        },
        state,
      );
    },
  };
});
